package com.rosario.juridico.ui.screens

import com.rosario.juridico.domain.AppMontos
import juridico5.composeapp.generated.resources.Res
import org.jetbrains.compose.resources.ExperimentalResourceApi

/**
 * TIPOS SOPORTADOS SEGÚN REQUERIMIENTO OFICIAL
 */
enum class TipoSellado {
    POR_MONTO,   // Cálculo porcentual: monto * alícuota
    FIJO_MT,     // Monto fijo expresado en Módulos Tributarios
    EXENTO       // Acto no gravado o con exención subjetiva/objetiva
}

/**
 * Representación del nomenclador oficial de Santa Fe (11 columnas).
 */
data class SelladoItem(
    val codigo: String,
    val nombre: String,
    val tipo: TipoSellado,
    val aliquotaStr: String,
    val minimoStr: String,
    val maximoStr: String, // Nueva columna agregada en 2026.3
    val exento: String,
    val adicionales: String,
    val fojas: String,
    val norma: String,
    val observaciones: String
)

/**
 * Recomendaciones contextuales para la práctica profesional.
 */
data class RecomendacionSellado(
    val titulo: String,
    val descripcion: String,
    val codigosRelacionados: List<String>
)

/**
 * Desglose detallado de la liquidación para transparencia del profesional.
 */
data class CalculoDetalle(
    val itemPrincipal: SelladoItem,
    val montoPrincipal: Double,
    val labelPrincipal: String,
    
    val montoAdicional: Double = 0.0,
    val labelAdicional: String = "",
    
    val montoReposicion: Double = 0.0,
    val labelReposicion: String = "",
    
    val montoFojas: Double = 0.0,
    val cantFojas: Int = 0,

    val montoFianza: Double = 0.0, // Agregado para locaciones
    
    val total: Double
)

@OptIn(ExperimentalResourceApi::class)
object SelladosParser {

    /**
     * Lee dinámicamente el archivo TXT ignorando comentarios y líneas vacías.
     * Soporta el nuevo formato de 11 columnas con MAXIMO.
     */
    suspend fun parsearBaseOficial(): List<SelladoItem> {
        val resultados = mutableListOf<SelladoItem>()
        try {
            val bytes = Res.readBytes("files/sellados_base.txt")
            val contenido = bytes.decodeToString()

            contenido.lines().forEach { linea ->
                val l = linea.trim()
                if (l.isEmpty() || l.startsWith("#")) return@forEach

                val partes = l.split("|")
                if (partes.size >= 11) {
                    try {
                        resultados.add(SelladoItem(
                            codigo        = partes[0].trim(),
                            nombre        = partes[1].trim(),
                            tipo          = parseTipo(partes[2].trim()),
                            aliquotaStr   = partes[3].trim(),
                            minimoStr     = partes[4].trim(),
                            maximoStr     = partes[5].trim(),
                            exento        = partes[6].trim(),
                            adicionales   = partes[7].trim(),
                            fojas         = partes[8].trim(),
                            norma         = partes[9].trim(),
                            observaciones = partes[10].trim()
                        ))
                    } catch (e: Exception) {
                        // Error de parseo en línea específica
                    }
                }
            }
        } catch (e: Exception) {
            println("Error crítico cargando sellados: ${e.message}")
        }
        return resultados
    }

    private fun parseTipo(tipo: String): TipoSellado {
        return try {
            TipoSellado.valueOf(tipo.uppercase())
        } catch (e: Exception) {
            TipoSellado.POR_MONTO
        }
    }
}

object SelladosRepository {
    private var cachedItems: List<SelladoItem>? = null

    suspend fun getItems(): List<SelladoItem> {
        if (cachedItems == null) {
            cachedItems = SelladosParser.parsearBaseOficial()
        }
        return cachedItems ?: emptyList()
    }

    fun findByCodigoSync(codigo: String): SelladoItem? {
        return cachedItems?.find { it.codigo == codigo }
    }

    /**
     * Obtiene recomendaciones basadas en el código buscado.
     */
    fun getRecomendaciones(codigo: String): RecomendacionSellado? {
        return when {
            // ORDINARIOS Y PROCESOS DE CONOCIMIENTO
            codigo in listOf("82101", "82068", "82077", "82086", "82095", "82299", "82138", "82147", "82148", "82149", "82150", "82151", "82152", "82153") -> RecomendacionSellado(
                titulo = "Juicios Ordinarios y Civiles",
                descripcion = "Procesos de conocimiento que requieren reposición de tasas (92217) y fojas de actuación (49047).",
                codigosRelacionados = listOf("92217", "49047")
            )
            // EJECUTIVOS Y EJECUCION DE SENTENCIA
            codigo in listOf("82156", "82165", "82174", "82183", "82192", "82154") -> RecomendacionSellado(
                titulo = "Procesos Ejecutivos",
                descripcion = "Los juicios ejecutivos y ejecuciones de sentencia llevan una reposición reducida (82226) y fojas para actuaciones.",
                codigosRelacionados = listOf("82226", "49047")
            )
            // SUCESORIOS Y PROCESOS HEREDITARIOS
            codigo in listOf("82129", "82217", "92032", "92217", "49135", "83061", "42019", "82160", "82161", "82162", "82163", "82164", "83062", "83063", "83064", "42092") -> RecomendacionSellado(
                titulo = "Juicios Sucesorios",
                descripcion = "Incluye inicio de demanda, declaratoria de herederos, incidentes (partición, colación) e inscripciones. Recuerde que el sellado de fojas suele ser el 25% de la tasa judicial.",
                codigosRelacionados = listOf("82217", "49047", "92217")
            )
            // CONCURSOS Y QUIEBRAS
            codigo in listOf("82013", "82022", "82031", "82235", "82244", "82253") -> RecomendacionSellado(
                titulo = "Concursos y Quiebras",
                descripcion = "Procesos universales sujetos a alícuotas específicas sobre el activo. Verifique el límite legal en preventivos.",
                codigosRelacionados = listOf("92217", "49047")
            )
            // LOCACIONES
            codigo in listOf("13001", "13064", "13143", "82110") -> RecomendacionSellado(
                titulo = "Locaciones y Desalojos",
                descripcion = "Si el contrato tiene garantía o fiador, corresponde el sellado de Fianza. En vivienda permanente (13143), si la fianza integra el mismo instrumento, puede estar exenta por Ley 14426.",
                codigosRelacionados = listOf("11095", "49047", "82226")
            )
            // CONTRATOS Y GARANTIAS
            codigo in listOf("11001", "11004", "11077", "11080", "11095", "12010", "16010") -> RecomendacionSellado(
                titulo = "Actos y Contratos",
                descripcion = "Recuerde agregar el sellado por fojas adicionales según la extensión del instrumento (original y copias).",
                codigosRelacionados = listOf("49047")
            )
            // FAMILIA
            codigo in listOf("92023", "92032", "92041", "92050", "92069") -> RecomendacionSellado(
                titulo = "Derecho de Familia",
                descripcion = "Los procesos de familia suelen tener montos fijos (MT) pero requieren reposición de ley y fojas.",
                codigosRelacionados = listOf("92217", "49047")
            )
            // MEDIDAS CAUTELARES
            codigo == "42091" -> RecomendacionSellado(
                titulo = "Medidas Cautelares",
                descripcion = "Inscripción de embargos o inhibiciones requieren sellado API proporcional.",
                codigosRelacionados = listOf("49047")
            )
            // IGPJ - PERSONAS JURÍDICAS
            codigo == "94074" -> RecomendacionSellado(
                titulo = "IGPJ - Personas Jurídicas",
                descripcion = "Los informes de IGPJ suelen requerir reposición judicial (92217 o 82226) si se presentan en expedientes, además de fojas adicionales.",
                codigosRelacionados = listOf("92217", "82226", "49047")
            )
            // REPOSICION Y FOJAS
            codigo in listOf("92217", "82226", "49047") -> RecomendacionSellado(
                titulo = "Tasas de Reposición y Fojas",
                descripcion = "Valores complementarios que se adicionan a la tasa principal o por cada foja de actuación judicial/administrativa.",
                codigosRelacionados = emptyList()
            )
            // EXENCIONES
            codigo in listOf("82901", "82902", "82903") -> RecomendacionSellado(
                titulo = "Exenciones",
                descripcion = "Actos exentos de tasa de justicia y sellos por normativa específica o garantía constitucional.",
                codigosRelacionados = emptyList()
            )
            // MEDIDAS AUTOSATISFACTIVAS
            codigo == "82155" -> RecomendacionSellado(
                titulo = "Medidas Autosatisfactivas",
                descripcion = "Procesos urgentes que tributan monto fijo en MT y requieren fojas de actuación.",
                codigosRelacionados = listOf("49047")
            )
            else -> null
        }
    }

    /**
     * Valor del Módulo Tributario (MT) vigente.
     */
    fun getValorMT(): Double = AppMontos.VALOR_MODULO_TRIBUTARIO
}

object SelladosEngine {

    /**
     * Redondeo financiero a 2 decimales (RoundingMode.HALF_UP) compatible con KMP.
     */
    private fun Double.roundToMoney(): Double {
        val factor = 100.0
        return kotlin.math.round(this * factor) / factor
    }
    
    /**
     * Lógica de cálculo automático de tasas, sellados y accesorios con precisión BigDecimal.
     */
    fun realizarCalculoIntegral(
        item: SelladoItem, 
        montoBase: Double, 
        cantFojas: Int = 0,
        valorMT: Double = AppMontos.VALOR_MODULO_TRIBUTARIO,
        tieneGarante: Boolean = false
    ): CalculoDetalle {
        val vMT = valorMT.roundToMoney()
        val mBase = montoBase.roundToMoney()

        // 1. Cálculo del ítem principal (Tasa o Sellado API)
        var principal = calcularIndividual(item, mBase, vMT)
        
        // --- NUEVO: Aplicar Tope Máximo ---
        val maximo = parseReferencia(item.maximoStr, vMT)
        if (maximo > 0.0) {
            principal = minOf(principal, maximo).roundToMoney()
        }

        val labelPrincipal = if (item.codigo.startsWith("8")) "Tasa de Justicia" else "Sellado API"

        // --- NUEVO: Fianza (5‰ sobre base si tiene garante) ---
        val montoFianza = if (tieneGarante) (mBase * 0.005).roundToMoney() else 0.0
        
        var montoAdic = 0.0
        var labelAdic = ""
        var montoRep = 0.0
        var labelRep = ""
        
        // 2. Cálculo de ítems adicionales robusto (Regex para extraer códigos de 5 dígitos)
        val codigosAdic = Regex("\\d{5}")
            .findAll(item.adicionales)
            .map { it.value }
            .toList()
            
        codigosAdic.forEach { cod ->
            val subItem = SelladosRepository.findByCodigoSync(cod)
            subItem?.let { si ->
                val m = if (si.aliquotaStr.contains("% de tasa")) {
                    val perc = (parseNumeric(si.aliquotaStr) / 100.0).roundToMoney()
                    val calc = (principal * perc).roundToMoney()
                    val min = parseReferencia(si.minimoStr, vMT)
                    maxOf(calc, min)
                } else {
                    calcularIndividual(si, mBase, vMT)
                }
                
                if (si.nombre.contains("Reposición", true)) {
                    montoRep = m.roundToMoney()
                    labelRep = si.nombre
                } else {
                    montoAdic = m.roundToMoney()
                    labelAdic = si.nombre
                }
            }
        }

        // 3. Cálculo de fojas complementarias
        var montoF = 0.0
        val codFojas = item.fojas.filter { it.isDigit() }.take(5)
        if (codFojas.length == 5) {
            val itemF = SelladosRepository.findByCodigoSync(codFojas)
            itemF?.let { f ->
                val factor = parseNumeric(f.aliquotaStr)
                montoF = (factor * cantFojas.toDouble() * vMT).roundToMoney()
            }
        }

        return CalculoDetalle(
            itemPrincipal = item,
            montoPrincipal = principal,
            labelPrincipal = labelPrincipal,
            montoAdicional = montoAdic,
            labelAdicional = labelAdic,
            montoReposicion = montoRep,
            labelReposicion = labelRep,
            montoFojas = montoF,
            cantFojas = cantFojas,
            montoFianza = montoFianza,
            total = (principal + montoAdic + montoRep + montoF + montoFianza).roundToMoney()
        )
    }

    private fun calcularIndividual(item: SelladoItem, montoBase: Double, valorMT: Double): Double {
        return when (item.tipo) {
            TipoSellado.EXENTO -> 0.0
            TipoSellado.FIJO_MT -> parseReferencia(item.minimoStr, valorMT)
            TipoSellado.POR_MONTO -> {
                val aliquota = parseAliquota(item.aliquotaStr)
                val calculado = (montoBase * aliquota).roundToMoney()
                val minimo = parseReferencia(item.minimoStr, valorMT)
                maxOf(calculado, minimo)
            }
        }.roundToMoney()
    }

    private fun parseReferencia(str: String, valorMT: Double): Double {
        val num = parseNumeric(str)
        return (if (str.contains("MT")) num * valorMT else num).roundToMoney()
    }

    private fun parseAliquota(str: String): Double {
        val num = parseNumeric(str)
        return when {
            str.contains("‰") -> (num / 1000.0)
            str.contains("%") -> (num / 100.0)
            else -> num
        }
    }

    private fun parseNumeric(str: String): Double {
        val filtered = str.trim().takeWhile { it.isDigit() || it == '.' || it == ',' }.replace(",", ".")
        return filtered.toDoubleOrNull() ?: 0.0
    }

    fun getFormulaVisual(item: SelladoItem): String {
        return when (item.tipo) {
            TipoSellado.EXENTO -> "Acto Exento"
            TipoSellado.FIJO_MT -> "Monto Fijo: ${item.minimoStr}"
            TipoSellado.POR_MONTO -> {
                var formula = "Alícuota: ${item.aliquotaStr}"
                if (item.minimoStr != "0") formula += " (Mínimo: ${item.minimoStr})"
                if (item.maximoStr != "0" && item.maximoStr.isNotEmpty()) formula += " (Máximo: ${item.maximoStr})"
                formula
            }
        }
    }
}

